// client/src/components/Header.jsx
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { resolveBadgeImage } from "../lib/badges";
import { unregisterCurrentPushDevice } from "../lib/nativePush";
import useLiveTournamentNavigation from "../hooks/useLiveTournamentNavigation";

function safeJsonParse(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function getToken() {
  return (
    localStorage.getItem("ga_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("streamsite_token") ||
    ""
  );
}

function getUser() {
  // Merge every known user cache instead of trusting one possibly stale key.
  // `user` is written last so profile/avatar edits win over older ga_user data.
  const cachedUsers = ["member_user", "ga_user", "user"]
    .map((key) => {
      const raw = localStorage.getItem(key) || "";
      const parsed = raw ? safeJsonParse(raw) : null;
      return parsed && typeof parsed === "object" ? parsed : null;
    })
    .filter(Boolean);

  if (cachedUsers.length) return Object.assign({}, ...cachedUsers);

  const username =
    localStorage.getItem("username") || localStorage.getItem("member_username") || "";
  const email = localStorage.getItem("email") || localStorage.getItem("member_email") || "";
  if (username || email) return { username, email };

  return null;
}

function persistUser(nextUser) {
  if (!nextUser || typeof nextUser !== "object") return;
  const serialized = JSON.stringify(nextUser);
  localStorage.setItem("user", serialized);
  localStorage.setItem("ga_user", serialized);
  window.dispatchEvent(new Event("auth:changed"));
}

async function clearAuth() {
  const token = getToken();
  await unregisterCurrentPushDevice(token).catch(() => {});
  [
    "ga_token",
    "token",
    "authToken",
    "jwt",
    "streamsite_token",
    "ga_user",
    "user",
    "member_user",
    "username",
    "email",
    "member_username",
    "member_email",
  ].forEach((k) => localStorage.removeItem(k));
  window.dispatchEvent(new Event("auth:changed"));
}


export default function Header({ variant = "default" }) {
  const nav = useNavigate();
  const loc = useLocation();

  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, "");
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  const [auth, setAuth] = useState(() => ({ token: getToken(), user: getUser() }));
  const [notifCount, setNotifCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState("");
  const [notifActionBusy, setNotifActionBusy] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  const [adminWorkspaceAllowed, setAdminWorkspaceAllowed] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const refresh = () => setAuth({ token: getToken(), user: getUser() });
    refresh();
    window.addEventListener("auth:changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth:changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    setAuth({ token: getToken(), user: getUser() });
  }, [loc.pathname]);

  // Pull the current profile from the server so the navigation always gets the
  // latest avatar, even after a refresh, sign-in on another device, or an older
  // localStorage value. This also repairs the two user cache keys in place.
  useEffect(() => {
    let cancelled = false;
    const token = auth.token;
    if (!token) return undefined;

    async function hydrateHeaderProfile() {
      try {
        const response = await fetch(`${API_BASE}/api/profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.ok || !data?.user || cancelled) return;

        const merged = { ...(getUser() || {}), ...data.user };
        persistUser(merged);
        setAuth((current) => ({ ...current, user: merged }));
      } catch {
        // Keep the cached name/avatar available if Render is waking up.
      }
    }

    hydrateHeaderProfile();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, auth.token]);

  useEffect(() => {
    let cancelled = false;
    if (!auth.token) {
      setAdminWorkspaceAllowed(false);
      return undefined;
    }

    async function checkAdminWorkspace() {
      try {
        const response = await fetch(`${API_BASE}/api/admin-operations/me`, {
          headers: { Authorization: `Bearer ${auth.token}` },
          cache: "no-store",
        });
        if (!cancelled) setAdminWorkspaceAllowed(response.ok);
      } catch {
        if (!cancelled) setAdminWorkspaceAllowed(false);
      }
    }

    checkAdminWorkspace();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, auth.token]);

  const signedIn = !!auth.token;
  const isDesktopStreamSidebar = variant === "streamDesktopSidebar" && !isMobile;

  async function loadNotificationsCount(showSpinner = false) {
    if (!auth.token) {
      setNotifCount(0);
      setNotifications([]);
      setNotifOpen(false);
      return;
    }

    if (showSpinner) setNotifLoading(true);
    setNotifError("");

    try {
      const r = await fetch(`${API_BASE}/api/notifications`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401) {
        setNotifCount(0);
        setNotifications([]);
        setNotifOpen(false);
        return;
      }
      if (!r.ok) throw new Error(data?.error || "Failed to load notifications");

      setNotifCount(Number(data?.unreadCount || 0));
      setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch (err) {
      if (showSpinner) setNotifError(err?.message || "Could not load notifications.");
    } finally {
      if (showSpinner) setNotifLoading(false);
    }
  }

  async function toggleNotifications() {
    const nextOpen = !notifOpen;
    setNotifOpen(nextOpen);
    if (nextOpen) await loadNotificationsCount(true);
  }

  async function markNotificationRead(notificationId) {
    if (!notificationId || !auth.token) return;
    try {
      await fetch(`${API_BASE}/api/notifications/mark-read`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: notificationId }),
      });
      setNotifications((current) =>
        current.map((notification) =>
          notification?.id === notificationId && !notification?.readAt
            ? { ...notification, readAt: Date.now(), read: true }
            : notification
        )
      );
      setNotifCount((count) => Math.max(0, count - 1));
    } catch {
      // Keep the popup usable even if marking read fails.
    }
  }

  async function markAllNotificationsRead() {
    if (!auth.token || notifActionBusy) return;
    setNotifActionBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/notifications/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!r.ok) throw new Error("Could not mark notifications read");
      const now = Date.now();
      setNotifications((current) =>
        current.map((notification) => ({ ...notification, readAt: notification?.readAt || now, read: true }))
      );
      setNotifCount(0);
    } catch (err) {
      setNotifError(err?.message || "Could not update notifications.");
    } finally {
      setNotifActionBusy(false);
    }
  }

  async function deleteReadNotification(notificationId) {
    if (!notificationId || !auth.token || notifActionBusy) return;
    setNotifActionBusy(true);
    setNotifError("");
    try {
      const r = await fetch(
        `${API_BASE}/api/notifications/${encodeURIComponent(notificationId)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${auth.token}` },
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data?.message || data?.error || "Could not remove notification");
      }
      setNotifications((current) =>
        current.filter((notification) => notification?.id !== notificationId)
      );
    } catch (err) {
      setNotifError(err?.message || "Could not remove notification.");
    } finally {
      setNotifActionBusy(false);
    }
  }

  async function clearReadNotifications() {
    if (!auth.token || notifActionBusy) return;
    const hasRead = notifications.some(
      (notification) => Boolean(notification?.readAt || notification?.read)
    );
    if (!hasRead) return;
    if (!window.confirm("Remove all notifications that have already been read?")) return;

    setNotifActionBusy(true);
    setNotifError("");
    try {
      const r = await fetch(`${API_BASE}/api/notifications/read`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data?.message || data?.error || "Could not clear read notifications");
      }
      setNotifications((current) =>
        current.filter((notification) => !(notification?.readAt || notification?.read))
      );
    } catch (err) {
      setNotifError(err?.message || "Could not clear read notifications.");
    } finally {
      setNotifActionBusy(false);
    }
  }

  async function openNotification(notification) {
    if (!notification) return;
    if (!notification.readAt) await markNotificationRead(notification.id);
    const href = String(notification.href || "").trim();
    if (href) {
      setNotifOpen(false);
      nav(href);
    }
  }

  useEffect(() => {
    loadNotificationsCount();
    const id = setInterval(loadNotificationsCount, 30000);
    return () => clearInterval(id);
  }, [auth.token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!notifOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setNotifOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [notifOpen]);

  const displayName = useMemo(() => {
    const u = auth.user || {};
    return u.displayName || u.username || u.name || u.email || "Account";
  }, [auth.user]);

  const isFounder =
    String(auth.user?.email || "").trim().toLowerCase() === "peteywhiskers@yahoo.com";
  const canOpenAdminWorkspace = isFounder || adminWorkspaceAllowed;
  const liveTournamentNav = useLiveTournamentNavigation({ includeTestMode: isFounder });

  const avatarLetter = (displayName || "A")[0]?.toUpperCase();
  const equippedBadgeImage = useMemo(
    () =>
      resolveBadgeImage(
        auth.user?.equippedBadgeKey,
        auth.user?.customBadgeUrl,
        API_BASE
      ),
    [API_BASE, auth.user?.customBadgeUrl, auth.user?.equippedBadgeKey]
  );

  const fixedWrap = {
    position: "fixed",
    top: isMobile ? "calc(8px + env(safe-area-inset-top, 0px))" : 12,
    left: 0,
    right: 0,
    zIndex: 9999,
    padding: isMobile ? "0 8px" : "0 12px",
    pointerEvents: "none",
  };

  const panel = {
    pointerEvents: "auto",
    maxWidth: 1120,
    margin: "0 auto",
    borderRadius: isMobile ? 16 : 18,
    padding: isMobile ? "10px 10px 10px" : "14px 14px 12px",
    border: "1px solid rgba(255,255,255,0.10)",
    background:
      "linear-gradient(180deg, rgba(88,88,88,0.58) 0%, rgba(24,24,24,0.78) 48%, rgba(10,10,10,0.84) 100%)",
    boxShadow:
      "0 22px 70px rgba(0,0,0,0.62), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.35)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  };

  const topRow = {
    display: "flex",
    alignItems: isMobile ? "stretch" : "center",
    justifyContent: "space-between",
    gap: isMobile ? 8 : 12,
    flexWrap: "wrap",
  };

  const brandPill = {
    display: "inline-flex",
    alignItems: "center",
    gap: isMobile ? 8 : 10,
    padding: isMobile ? "8px 12px" : "9px 14px",
    borderRadius: 999,
    cursor: "pointer",
    userSelect: "none",
    border: "1px solid rgba(255, 90, 90, 0.40)",
    background:
      "linear-gradient(180deg, rgba(255,30,30,0.95) 0%, rgba(165,0,0,0.92) 65%, rgba(120,0,0,0.90) 100%)",
    boxShadow:
      "0 18px 40px rgba(255,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.16)",
  };

  const brandDot = {
    width: isMobile ? 9 : 10,
    height: isMobile ? 9 : 10,
    borderRadius: 999,
    background: "rgba(255,255,255,0.90)",
    boxShadow: "0 0 0 3px rgba(0,0,0,0.20) inset, 0 0 18px rgba(255,255,255,0.18)",
  };

  const brandText = {
    fontWeight: 900,
    fontSize: isMobile ? 14 : 16,
    letterSpacing: 0.2,
    color: "rgba(255,255,255,0.95)",
    textShadow: "0 2px 10px rgba(0,0,0,0.45)",
  };

  const authRow = {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 8 : 10,
    flexWrap: "wrap",
    justifyContent: isMobile ? "flex-start" : "flex-end",
    width: isMobile ? "100%" : "auto",
  };

  const btnBase = {
    padding: isMobile ? "9px 12px" : "10px 16px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: isMobile ? 14 : 15,
    letterSpacing: 0.2,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.07)",
  };

  const btnPrimaryRed = {
    ...btnBase,
    border: "1px solid rgba(255, 70, 70, 0.40)",
    background:
      "linear-gradient(180deg, rgba(255,50,50,0.92) 0%, rgba(170,0,0,0.88) 60%, rgba(120,0,0,0.86) 100%)",
    boxShadow: "0 14px 30px rgba(255,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.10)",
    minWidth: isMobile ? 120 : 150,
  };

  const btnBlue = {
    ...btnBase,
    background: "rgba(0, 160, 255, 0.75)",
    minWidth: isMobile ? 110 : 120,
  };

  const profileBtn = {
    ...btnBase,
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: isMobile ? 160 : 170,
    justifyContent: "center",
    flex: isMobile ? "1 1 auto" : "0 0 auto",
  };

  const notifBtn = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: isMobile ? "9px 11px" : "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
  };

  const notifBadge = {
    position: "absolute",
    top: -6,
    right: -6,
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 999,
    background: "rgba(255, 40, 40, 0.92)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 1000,
    border: "2px solid rgba(0,0,0,0.55)",
  };

  const avatarWrap = {
    position: "relative",
    width: isMobile ? 26 : 28,
    height: isMobile ? 26 : 28,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.25)",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    overflow: "hidden",
    background: "rgba(0,0,0,0.18)",
  };

  const navRow = {
    marginTop: isMobile ? 10 : 12,
    display: "flex",
    justifyContent: "center",
    gap: isMobile ? 8 : 10,
    flexWrap: "wrap",
  };

  const linkBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "8px 11px" : "9px 12px",
    borderRadius: 999,
    textDecoration: "none",
    fontWeight: 800,
    fontSize: isMobile ? 13 : 14,
    letterSpacing: 0.2,
    color: "rgba(255,255,255,0.90)",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.05)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
    whiteSpace: "nowrap",
  };

  const linkActive = {
    color: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(255, 40, 40, 0.45)",
    background: "rgba(255, 40, 40, 0.18)",
    boxShadow: "0 10px 22px rgba(255,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.08)",
  };

  const liveTournamentLink = liveTournamentNav?.isTest
    ? {
        border: "1px solid rgba(207,137,255,0.62)",
        background: "linear-gradient(180deg, rgba(119,45,166,.54), rgba(55,18,82,.58))",
        boxShadow: "0 10px 28px rgba(116,35,165,.24), inset 0 1px 0 rgba(255,255,255,.08)",
        color: "#f4dcff",
      }
    : {
        border: "1px solid rgba(255,74,74,0.68)",
        background: "linear-gradient(180deg, rgba(216,20,28,.62), rgba(112,0,7,.66))",
        boxShadow: "0 10px 28px rgba(220,0,12,.24), inset 0 1px 0 rgba(255,255,255,.08)",
        color: "#fff",
      };

  const spacerHeight = isMobile ? "calc(250px + env(safe-area-inset-top, 0px))" : 110;

  const sidebarWrap = {
    position: "sticky",
    top: 12,
    width: 232,
    minWidth: 232,
    alignSelf: "flex-start",
    paddingLeft: 12,
    zIndex: 20,
  };

  const sidebarPanel = {
    pointerEvents: "auto",
    borderRadius: 26,
    padding: 14,
    background:
      "linear-gradient(180deg, rgba(45,48,60,0.92) 0%, rgba(25,26,35,0.94) 100%)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 20px 50px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.10)",
    backdropFilter: "blur(14px)",
  };

  const sideBrand = {
    ...brandPill,
    width: "100%",
    justifyContent: "center",
    marginBottom: 16,
  };

  const sideNav = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 18,
  };

  const sideLinkBase = {
    ...linkBase,
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "16px 18px",
    fontSize: 16,
    borderRadius: 20,
    background: "rgba(255,255,255,0.05)",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.12,
    minHeight: 60,
    height: "auto",
    textAlign: "left",
  };

  const sideSection = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginTop: 12,
  };

  function formatNotificationTime(value) {
    const time = Number(value || 0);
    if (!time) return "";
    try {
      return new Date(time).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }

  function renderNotificationsPopup() {
    if (!notifOpen || !signedIn) return null;

    return (
      <div
        style={{ position: "fixed", inset: 0, zIndex: 10040, pointerEvents: "auto" }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setNotifOpen(false);
        }}
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-label="Notifications"
          style={{
            position: "absolute",
            top: isMobile ? 154 : 92,
            right: isMobile ? 10 : 24,
            left: isMobile ? 10 : "auto",
            width: isMobile ? "auto" : 410,
            maxHeight: isMobile ? "calc(100vh - 172px)" : "min(620px, calc(100vh - 112px))",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            borderRadius: 20,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "linear-gradient(180deg, rgba(31,16,19,0.98), rgba(7,8,12,0.99))",
            boxShadow: "0 28px 90px rgba(0,0,0,0.72), inset 0 1px 0 rgba(255,255,255,0.07)",
            backdropFilter: "blur(16px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "16px 16px 13px",
              borderBottom: "1px solid rgba(255,255,255,0.09)",
            }}
          >
            <div>
              <div style={{ fontWeight: 1000, fontSize: 19, color: "#fff" }}>Notifications</div>
              <div style={{ marginTop: 3, fontSize: 12, color: "rgba(255,255,255,0.58)" }}>
                {notifCount > 0 ? `${notifCount} unread` : "You're all caught up"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              {notifCount > 0 ? (
                <button
                  type="button"
                  disabled={notifActionBusy}
                  onClick={markAllNotificationsRead}
                  style={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.86)",
                    borderRadius: 999,
                    padding: "7px 10px",
                    cursor: notifActionBusy ? "wait" : "pointer",
                    fontWeight: 800,
                    fontSize: 12,
                  }}
                >
                  Mark all read
                </button>
              ) : null}
              {notifications.some((notification) => Boolean(notification?.readAt || notification?.read)) ? (
                <button
                  type="button"
                  disabled={notifActionBusy}
                  onClick={clearReadNotifications}
                  style={{
                    border: "1px solid rgba(255,110,110,0.24)",
                    background: "rgba(120,0,0,0.18)",
                    color: "#ffb3b3",
                    borderRadius: 999,
                    padding: "7px 10px",
                    cursor: notifActionBusy ? "wait" : "pointer",
                    fontWeight: 800,
                    fontSize: 12,
                  }}
                >
                  Clear read
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setNotifOpen(false)}
                aria-label="Close notifications"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          </div>

          <div style={{ overflowY: "auto", padding: 10 }}>
            {notifLoading ? (
              <div style={{ padding: 24, textAlign: "center", color: "rgba(255,255,255,0.68)" }}>
                Loading notifications…
              </div>
            ) : notifError ? (
              <div style={{ padding: 18, borderRadius: 14, background: "rgba(255,50,50,0.10)", color: "#ffb6b6" }}>
                {notifError}
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: "rgba(255,255,255,0.62)" }}>
                No notifications yet.
              </div>
            ) : (
              notifications.slice(0, 20).map((notification) => {
                const unread = !notification?.readAt;
                const canOpen = Boolean(String(notification?.href || "").trim());
                return (
                  <div
                    key={notification.id}
                    role={canOpen || unread ? "button" : undefined}
                    tabIndex={canOpen || unread ? 0 : -1}
                    onClick={() => openNotification(notification)}
                    onKeyDown={(event) => {
                      if ((event.key === "Enter" || event.key === " ") && (canOpen || unread)) {
                        event.preventDefault();
                        openNotification(notification);
                      }
                    }}
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      border: unread ? "1px solid rgba(255,72,72,0.30)" : "1px solid rgba(255,255,255,0.08)",
                      background: unread ? "rgba(118,12,20,0.22)" : "rgba(255,255,255,0.035)",
                      borderRadius: 15,
                      padding: "13px 14px",
                      marginBottom: 8,
                      color: "#fff",
                      cursor: canOpen || unread ? "pointer" : "default",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {unread ? (
                            <span style={{ width: 8, height: 8, flex: "0 0 auto", borderRadius: 999, background: "#ff3434", boxShadow: "0 0 12px rgba(255,52,52,0.7)" }} />
                          ) : null}
                          <div style={{ fontWeight: 950, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {notification.title || "Notification"}
                          </div>
                        </div>
                        {notification.body || notification.textPreview ? (
                          <div style={{ marginTop: 6, color: "rgba(255,255,255,0.72)", fontSize: 13, lineHeight: 1.45 }}>
                            {notification.body || notification.textPreview}
                          </div>
                        ) : null}
                        <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                          <span style={{ color: "rgba(255,255,255,0.42)", fontSize: 11 }}>
                            {formatNotificationTime(notification.createdAt)}
                          </span>
                          {canOpen ? (
                            <span style={{ color: "#ff8d8d", fontSize: 12, fontWeight: 900 }}>Open →</span>
                          ) : null}
                        </div>
                      </div>
                      {!unread ? (
                        <button
                          type="button"
                          disabled={notifActionBusy}
                          aria-label={`Remove ${notification.title || "notification"}`}
                          title="Remove read notification"
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteReadNotification(notification.id);
                          }}
                          style={{
                            flex: "0 0 auto",
                            width: 30,
                            height: 30,
                            borderRadius: 999,
                            border: "1px solid rgba(255,110,110,0.24)",
                            background: "rgba(120,0,0,0.18)",
                            color: "#ffb3b3",
                            cursor: notifActionBusy ? "wait" : "pointer",
                            fontSize: 18,
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    );
  }

  if (isDesktopStreamSidebar) {
    return (
      <>
        <aside style={sidebarWrap}>
        <div style={sidebarPanel}>
          <div
            onClick={() => nav("/")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") nav("/");
            }}
            style={sideBrand}
            aria-label="Go to home"
            title="Go to home"
          >
            <span style={brandDot} />
            <span style={brandText}>GAStreaming.live</span>
          </div>

          <nav style={sideNav} aria-label="Primary navigation">
            {liveTournamentNav ? (
              <NavLink
                to={liveTournamentNav.destinationPath}
                style={({ isActive }) =>
                  isActive
                    ? { ...sideLinkBase, ...liveTournamentLink, ...linkActive }
                    : { ...sideLinkBase, ...liveTournamentLink }
                }
                title={liveTournamentNav.isTest ? "Return to Founder tournament test" : "Watch the live GAStreaming tournament"}
              >
                <span style={{ display: "block", width: "100%", lineHeight: 1.12 }}>
                  {liveTournamentNav.label}
                </span>
              </NavLink>
            ) : null}
            {[
              ["Channels", "/streams"],
              ["Patch Note Hub", "/patches"],
              ["The Man Cave", "/man-cave"],
              ["Got Questions?", "/contact"],
              ["Feed", "/feed"],
            ].map(([label, to]) => (
              <NavLink
                key={to}
                to={to}
                style={({ isActive }) =>
                  isActive ? { ...sideLinkBase, ...linkActive } : sideLinkBase
                }
              >
                <span
                  style={{
                    display: "block",
                    width: "100%",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    lineHeight: 1.12,
                    fontSize: 16,
                  }}
                >
                  {label}
                </span>
              </NavLink>
            ))}
          </nav>

          <div style={sideSection}>
            {signedIn ? (
              <>
                <button
                  onClick={() => nav("/me")}
                  style={{
                    ...profileBtn,
                    width: "100%",
                    justifyContent: "flex-start",
                    borderRadius: 18,
                    padding: "14px 16px",
                  }}
                >
                  <span style={avatarWrap} title={equippedBadgeImage ? "Equipped badge" : "No badge equipped"}>
                    {equippedBadgeImage ? (
                      <img
                        src={equippedBadgeImage}
                        alt="Equipped badge"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      avatarLetter
                    )}
                  </span>
                  <span style={{ display: "grid", gap: 2, minWidth: 0, textAlign: "left" }}>
                    <span style={{ fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {displayName}
                    </span>
                    <span style={{ color: "#ffd65c", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap" }}>
                      ⛽ {Math.max(0, Number(auth.user?.gasPoints || 0)).toLocaleString()} GAS Points
                    </span>
                  </span>
                </button>

                <button
                  onClick={toggleNotifications}
                  style={{
                    ...notifBtn,
                    width: "100%",
                    justifyContent: "flex-start",
                    borderRadius: 18,
                    padding: "14px 16px",
                    position: "relative",
                  }}
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>🔔</span>
                  <span style={{ fontWeight: 900 }}>Notifications</span>
                  {notifCount > 0 ? (
                    <span style={{ ...notifBadge, top: 10, right: 10 }}>
                      {notifCount > 99 ? "99+" : notifCount}
                    </span>
                  ) : null}
                </button>

                {canOpenAdminWorkspace ? (
                  <button
                    type="button"
                    onClick={() => nav("/admin")}
                    style={{
                      ...btnPrimaryRed,
                      width: "100%",
                      justifyContent: "center",
                      padding: "14px 16px",
                      borderRadius: 18,
                    }}
                    title="Open the GAStreaming Operations Portal"
                  >
                    🛡️ Admin Operations
                  </button>
                ) : null}

                <button
                  onClick={async () => {
                    await clearAuth();
                    nav("/signin");
                  }}
                  style={{
                    ...btnBlue,
                    width: "100%",
                    justifyContent: "center",
                    padding: "16px 18px",
                    borderRadius: 18,
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => nav("/signin")}
                  style={{
                    ...btnBase,
                    width: "100%",
                    justifyContent: "center",
                    padding: "14px 16px",
                    borderRadius: 18,
                  }}
                >
                  Sign in
                </button>
                <button
                  onClick={() => nav("/register")}
                  style={{
                    ...btnPrimaryRed,
                    width: "100%",
                    justifyContent: "center",
                    padding: "14px 16px",
                    borderRadius: 18,
                  }}
                >
                  Create account
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
        {renderNotificationsPopup()}
      </>
    );
  }

  return (
    <>
      <div style={fixedWrap}>
        <div style={panel}>
          <div style={topRow}>
            <div
              onClick={() => nav("/")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") nav("/");
              }}
              style={brandPill}
              aria-label="Go to home"
              title="Go to home"
            >
              <span style={brandDot} />
              <span style={brandText}>GAStreaming.live</span>
            </div>

            <div style={authRow}>
              {signedIn ? (
                <>
                  <button onClick={() => nav("/me")} style={profileBtn}>
                    <span style={avatarWrap} title={equippedBadgeImage ? "Equipped badge" : "No badge equipped"}>
                    {equippedBadgeImage ? (
                      <img
                        src={equippedBadgeImage}
                        alt="Equipped badge"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      avatarLetter
                    )}
                  </span>
                    <span style={{ display: "grid", gap: 2, minWidth: 0, textAlign: "left" }}>
                    <span style={{ fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {displayName}
                    </span>
                    <span style={{ color: "#ffd65c", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap" }}>
                      ⛽ {Math.max(0, Number(auth.user?.gasPoints || 0)).toLocaleString()} GAS Points
                    </span>
                  </span>
                  </button>

                  <button
                    onClick={toggleNotifications}
                    style={notifBtn}
                    aria-label="Notifications"
                    title="Notifications"
                  >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>🔔</span>
                    {notifCount > 0 ? (
                      <span style={notifBadge}>{notifCount > 99 ? "99+" : notifCount}</span>
                    ) : null}
                  </button>

                  {isFounder ? (
                    <button
                      type="button"
                      onClick={() => nav("/admin")}
                      style={{ ...btnPrimaryRed, padding: "10px 14px" }}
                      title="Open the GAStreaming Operations Portal"
                    >
                      {isMobile ? "Admin" : isFounder ? "Admin Operations" : "My Admin"}
                    </button>
                  ) : null}

                  <button
                    onClick={() => {
                      clearAuth();
                      nav("/signin");
                    }}
                    style={btnBlue}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => nav("/signin")} style={btnBase}>
                    Sign in
                  </button>
                  <button onClick={() => nav("/register")} style={btnPrimaryRed}>
                    Create account
                  </button>
                </>
              )}
            </div>
          </div>

          <nav style={navRow} aria-label="Primary navigation">
            {liveTournamentNav ? (
              <NavLink
                to={liveTournamentNav.destinationPath}
                style={({ isActive }) =>
                  isActive
                    ? { ...linkBase, ...liveTournamentLink, ...linkActive }
                    : { ...linkBase, ...liveTournamentLink }
                }
                title={liveTournamentNav.isTest ? "Return to Founder tournament test" : "Watch the live GAStreaming tournament"}
              >
                {liveTournamentNav.label}
              </NavLink>
            ) : null}
            {[
              ["Channels", "/streams"],
              ["Patch Note Hub", "/patches"],
              ["The Man Cave", "/man-cave"],
              ["Got Questions?", "/contact"],
              ["Feed", "/feed"],
            ].map(([label, to]) => (
              <NavLink
                key={to}
                to={to}
                style={({ isActive }) => (isActive ? { ...linkBase, ...linkActive } : linkBase)}
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <style>{`
            @media (max-width: 640px){
              nav[aria-label="Primary navigation"]{
                display: grid !important;
                grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                gap: 6px !important;
                width: 100% !important;
                overflow: visible !important;
                padding-bottom: 0 !important;
              }
              nav[aria-label="Primary navigation"] > a{
                width: 100% !important;
                min-width: 0 !important;
                box-sizing: border-box !important;
                padding: 8px 5px !important;
                font-size: 11px !important;
                line-height: 1.15 !important;
                white-space: normal !important;
                text-align: center !important;
                overflow-wrap: anywhere !important;
              }
            }
            @media (max-width: 380px){
              nav[aria-label="Primary navigation"] > a{
                font-size: 10px !important;
                padding: 7px 4px !important;
              }
            }
          `}</style>
        </div>
      </div>

      {renderNotificationsPopup()}

      <div aria-hidden="true" style={{ height: spacerHeight }} />
    </>
  );
}